import {useShallow} from 'zustand/shallow';
import {useEffect} from 'react';
import {SocialPlatform} from '@/types/platforms';
import assertUnreachable from '@/utils/assertUnreachable';
import {useOnboardingStore, OnboardingState} from './OnboardingStore';
import {STEPS, type Step} from './onboardingSteps';
import {useUserData} from '@/hooks/useUserData';
import {meetsAgeRequirement} from '@/utils/calculateAge';
import {hasProfilePicture} from './isAnonymousAvatarUrl';

export const MAX_SPECIALTIES = 3;

export function isStepValid(
  step: Step,
  state: OnboardingState,
  connectedPlatforms: SocialPlatform[]
) {
  switch (step) {
    case STEPS.NAME_AND_BIRTHDATE: {
      const isNameValid =
        Boolean(state.firstName?.trim()) && Boolean(state.lastName?.trim());

      return isNameValid && meetsAgeRequirement(state.birthdate);
    }
    case STEPS.CREATOR_SEGMENT:
      return Boolean(state.segment);
    case STEPS.LOCATION:
      return Boolean(state.location);
    case STEPS.GENDER:
      return state.gender !== undefined;
    case STEPS.SPECIALTIES:
      return (
        state.specialties.size > 0 && state.specialties.size <= MAX_SPECIALTIES
      );
    case STEPS.CAPABILITIES:
      return state.capabilities.size > 0;
    case STEPS.BIO:
      return state.bio !== undefined;
    case STEPS.PORTFOLIO:
      return (
        state.portfolioMediaItems.length > 0 &&
        hasProfilePicture(state.profilePictureUrl)
      );
    case STEPS.COLLABORATION_RATE:
      return state.minCollaborationRate !== undefined;
    case STEPS.COMPLETION:
    case STEPS.INSTAGRAM_INSTRUCTIONS:
    case STEPS.REONBOARDING:
      return true;
    case STEPS.SYNC_PLATFORMS:
      return (
        state.segment === 'creator' ||
        (state.segment === 'influencer' && connectedPlatforms.length > 0)
      );
    default:
      return assertUnreachable(step);
  }
}

function useStepValidation() {
  const [
    step,
    firstName,
    lastName,
    birthdate,
    segment,
    gender,
    location,
    specialties,
    capabilities,
    portfolioMediaItems,
    profilePictureUrl,
    bio,
  ] = useOnboardingStore(
    useShallow((state) => [
      state.step,
      state.firstName,
      state.lastName,
      state.birthdate,
      state.segment,
      state.gender,
      state.location,
      state.specialties,
      state.capabilities,
      state.portfolioMediaItems,
      state.profilePictureUrl,
      state.bio,
    ])
  );

  const {connectedPlatforms} = useUserData({});

  const [setStepValidity] = useOnboardingStore(
    useShallow((state) => [state.setStepValidity])
  );

  useEffect(() => {
    const state = useOnboardingStore.getState();
    setStepValidity(isStepValid(step, state, connectedPlatforms));
  }, [
    step,
    firstName,
    lastName,
    birthdate,
    segment,
    gender,
    location,
    specialties,
    capabilities,
    setStepValidity,
    connectedPlatforms,
    portfolioMediaItems,
    profilePictureUrl,
    bio,
  ]);
}

export default useStepValidation;
